import { h } from '../ui/icons';
import { fontStack } from './fonts';
import { inkById, INK_SHADOW_EM } from './inks';
import { baseline, logoCentre, logoPlace, placeOf, pointPlace, textCorners, zoneCorners, type LogoLayout, type TextLayout, type ZoneBox } from './layout';
import { LOGO_PATHS, LOGO_VIEWBOX } from './logo';
import { centreOnTemple, FADE_FROM, hingeImageBox, mirroredFor, templeScene, VIEW_RATIO, type HingePlacement, type Scene } from './scene';
import hingeUrl from '../../assets-source/hinge/hinge-nickel.webp?url';
import { isEditable, LOCKED_SLOT, ROT_MAX, roundPlace, slotOf, tooLong, type EngraveState, type FreePlace, type SlotKey, type TempleFace, type TempleSide } from './state';
import type { TempleImage } from './templeImage';

/**
 * A110Z — one temple of the pair, drawn as SVG: the model's temple photo (recoloured in the chosen acetate), the
 * engraving zone, and the engraving itself — the wordmark on the inner left temple, the texts elsewhere.
 *
 * A110Z2: real letter heights (mm), hollow O in the wordmark (even-odd), the hinge on the inner view.
 * A110Z3 (David, 7 octobre):
 *   • the real temple photo, sharp (no matte filter, no blurred band);
 *   • « placer, tourner la gravure où ils veulent sur la branche » — the active text is dragged anywhere on the
 *     temple (its centre stays on it), turned with two fingers, and outlined while placed by hand: sage, or coral
 *     when it leaves the temple or touches the hinge;
 *   • symbols in « Mata Symboles » (listed first, letters fall through to the typeface).
 * The geometry lives in `scene.ts`, shared with the order sheet.
 *
 * Interaction: a press on the card selects its slot (`onSelect`); a tap without moving activates it
 * (`onActivate`: keyboard, field focus); a drag moves the text (`onPlace`), two fingers turn it.
 *
 * A203 (David, 11 octobre : « je voudrais tous pouvoir les tourner ») : a round handle at the end of the selected text
 * (or logo) turns it with the mouse or one finger, around its centre, snapping level near 0°, within ±45°.
 */
export interface TempleCardWords {
  temple: (side: TempleSide) => string;
  face: (face: TempleFace) => string;
  ghost: string;
  slot: (slot: SlotKey) => string;
  /** A203: tooltip of the turn handle. */
  spin?: string;
}

export interface TempleView {
  templeId: string;
  face: TempleFace;
  image: TempleImage | null;
  state: EngraveState;
  active: SlotKey | null;
}

export interface TempleCardOptions {
  side: TempleSide;
  words: TempleCardWords;
  /** Pointer down on the card: the slot becomes the active one. */
  onSelect?: (slot: SlotKey) => void;
  /** Tap / click without a drag (fires after `onSelect`): open the keyboard, focus the field. */
  onActivate?: (slot: SlotKey) => void;
  /** The text was moved or turned by hand. */
  onPlace?: (slot: SlotKey, place: FreePlace) => void;
  /** Card aspect ratio (width / height of the drawing). */
  ratio?: number;
  /**
   * A201 « loupe » : the drawing enlarged this many times around the text (or the logo) of the view, fixed when
   * the view changes (not while it is dragged, so that the finger keeps its text).
   */
  zoom?: number;
}

const SVG = 'http://www.w3.org/2000/svg';
const XLINK = 'http://www.w3.org/1999/xlink';
export { VIEW_RATIO, mirroredFor };
const COLOUR_EMPTY = 'rgba(255,248,236,.85)';
const COLOUR_ACTIVE = '#9fc58a';
const COLOUR_WARN = '#d2694a';
/** How far past the card the photo may show (fraction of the card's height, above and below). */
const BLEED = 0.25;
/** Two-finger turn: snaps to the temple's axis within this many degrees. */
const ROT_SNAP = 2.5;
/** A203: radius of the turn handle and its gap past the end of the text (screen px). */
const SPIN_R_PX = 10;
const SPIN_GAP_PX = 12;

function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

interface Drag {
  pointerId: number;
  startX: number;
  startY: number;
  start: FreePlace;
  centre: { x: number; y: number };
  moved: boolean;
}

export class TempleCard {
  readonly root = h('div', 'mt-temple');
  readonly svg = el('svg', { class: 'mt-temple-svg', 'aria-hidden': 'true', focusable: 'false' });
  private readonly faceLabel = h('span', 'mt-temple-face');
  private readonly imageG = el('g', { class: 'mt-temple-img' });
  private readonly imageT = el('g');
  private readonly image = el('image', { preserveAspectRatio: 'none' });
  private readonly maskRect = el('rect');
  private readonly hingeG = el('g', { class: 'mt-temple-hinge' });
  private readonly zonePoly = el('polygon', { class: 'mt-temple-zone', fill: 'none', 'vector-effect': 'non-scaling-stroke' });
  private readonly textBox = el('polygon', { class: 'mt-temple-textbox', fill: 'none', 'vector-effect': 'non-scaling-stroke' });
  private readonly ghost = el('text', { class: 'mt-temple-ghost', 'text-anchor': 'middle', 'dominant-baseline': 'middle' });
  private readonly textG = el('g', { class: 'mt-temple-text' });
  private readonly shadowText = el('text');
  private readonly mainText = el('text');
  private readonly logoG = el('g', { class: 'mt-temple-logo' });
  private readonly hit = el('rect', { class: 'mt-temple-hit', fill: 'transparent' });
  /** A203: the turn handle (on top of the hit area), and the gesture in progress. */
  private readonly spinG = el('g', { class: 'mt-temple-spin' });
  private readonly spinDisc = el('circle', { class: 'mt-temple-spin-disc', 'vector-effect': 'non-scaling-stroke' });
  private readonly spinIcon = el('path', { class: 'mt-temple-spin-icon', fill: 'none', 'vector-effect': 'non-scaling-stroke' });
  private spin: { pointerId: number; centre: { x: number; y: number }; angle0: number; rot0: number; start: FreePlace } | null = null;
  private readonly uid = Math.random().toString(36).slice(2, 8);
  private readonly ratio: number;
  private view: TempleView | null = null;
  private scene: Scene | null = null;
  private imageUrl = '';
  private drag: Drag | null = null;
  /** Second finger of a turn gesture: its id, the starting angle between the fingers and the starting tilt. */
  private turn: { pointerId: number; angle0: number; rot0: number } | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private dragged = false;
  /** Loupe: the point the enlarged view is centred on, and for which view (slot · model). */
  private focus: { x: number; y: number; key: string } | null = null;
  private refocus = false;

  constructor(private readonly opts: TempleCardOptions) {
    this.ratio = opts.ratio ?? VIEW_RATIO;
    this.root.dataset.side = opts.side;
    const head = h('div', 'mt-temple-head');
    head.append(h('span', 'mt-temple-title', opts.words.temple(opts.side)), this.faceLabel);
    // Fade of the far end of the temple (image only; the engraving stays crisp).
    const defs = el('defs');
    const grad = el('linearGradient', { id: `mt-tfade-${this.uid}`, x1: 0, y1: 0, x2: 1, y2: 0 });
    grad.append(el('stop', { offset: 0, 'stop-color': '#fff' }), el('stop', { offset: FADE_FROM, 'stop-color': '#fff' }), el('stop', { offset: 1, 'stop-color': '#fff', 'stop-opacity': 0 }));
    const mask = el('mask', { id: `mt-tmask-${this.uid}`, maskUnits: 'userSpaceOnUse' });
    this.maskRect.setAttribute('fill', `url(#mt-tfade-${this.uid})`);
    mask.append(this.maskRect);
    defs.append(grad, mask);
    // The mask is read in the group's own (untransformed) space: the mirror lives on the inner group.
    this.imageG.setAttribute('mask', `url(#mt-tmask-${this.uid})`);
    this.imageT.append(this.image);
    this.imageG.append(this.imageT);
    buildHinge(this.hingeG);
    // Wordmark paths (filled in the ink colour by `render`). The O and the inner contours are holes: even-odd.
    const logoInner = el('g', { class: 'mt-temple-logo-paths', 'fill-rule': 'evenodd' });
    for (const d of LOGO_PATHS) logoInner.append(el('path', { d }));
    this.logoG.append(logoInner);
    this.shadowText.setAttribute('class', 'mt-temple-shadow');
    this.mainText.setAttribute('class', 'mt-temple-main');
    this.textG.append(this.shadowText, this.mainText);
    const spinTitle = el('title'); spinTitle.textContent = opts.words.spin ?? '';
    this.spinG.append(spinTitle, this.spinDisc, this.spinIcon);
    this.spinG.style.display = 'none';
    this.svg.append(defs, this.imageG, this.hingeG, this.zonePoly, this.textBox, this.ghost, this.textG, this.logoG, this.hit, this.spinG);
    this.root.append(head, this.svg);
    this.root.style.setProperty('--mt-temple-ratio', String(this.ratio));
    this.installPointer();
    this.installSpin();
    // A203: the handle keeps its size on screen when the card is resized.
    if (typeof ResizeObserver !== 'undefined') {
      let lastWidth = 0;
      new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (Math.abs(width - lastWidth) < 1) return;
        lastWidth = width;
        if (this.view) this.render();
      }).observe(this.svg);
    }
  }

  /** Slot this card shows in its current face. */
  get slot(): SlotKey | null {
    return this.view ? slotOf(this.opts.side, this.view.face) : null;
  }

  /** The geometry last drawn (tests, order sheet). */
  get currentScene(): Scene | null {
    return this.scene;
  }

  update(view: TempleView): void {
    this.view = view;
    this.faceLabel.textContent = this.opts.words.face(view.face);
    this.render();
  }

  /** Re-draws with the fonts now loaded (same view). */
  refresh(): void {
    if (this.view) this.render();
  }

  private render(): void {
    const view = this.view!;
    const sc = templeScene({ templeId: view.templeId, side: this.opts.side, face: view.face, image: view.image, state: view.state, ratio: this.ratio });
    this.scene = sc;
    const { W, H, box, mirrored, slot } = sc;
    const v = this.opts.zoom && this.opts.zoom > 1 ? this.zoomView(sc, view) : sc.view;
    this.root.style.setProperty('--mt-temple-ratio', (v.w / v.h).toFixed(3));
    this.svg.setAttribute('viewBox', `${v.x.toFixed(1)} ${v.y.toFixed(1)} ${v.w.toFixed(1)} ${v.h.toFixed(1)}`);
    // Fade mask: set to the view explicitly (the default region is measured from the origin, not the viewBox).
    for (const r of [this.maskRect, this.svg.querySelector('mask')!]) {
      // A201: the photo may show a little past the card (a quarter of its height), never the rest of a tall temple
      // (Velvet Noir: its side lens under the engraving band spilled over the panels below).
      r.setAttribute('x', String(v.x)); r.setAttribute('y', String(v.y - v.h * BLEED)); r.setAttribute('width', String(v.w)); r.setAttribute('height', String(v.h * (1 + 2 * BLEED)));
    }
    const grad = this.svg.querySelector('linearGradient')!;
    if (mirrored) { grad.setAttribute('x1', '0'); grad.setAttribute('x2', '1'); } else { grad.setAttribute('x1', '1'); grad.setAttribute('x2', '0'); }
    this.hit.setAttribute('x', String(v.x)); this.hit.setAttribute('y', String(v.y));
    this.hit.setAttribute('width', String(v.w)); this.hit.setAttribute('height', String(v.h));

    // Photo
    const img = view.image;
    if (img) {
      if (this.imageUrl !== img.url) {
        this.imageUrl = img.url;
        this.image.setAttribute('href', img.url);
        this.image.setAttributeNS(XLINK, 'xlink:href', img.url);
      }
      this.image.setAttribute('width', String(W)); this.image.setAttribute('height', String(H));
      if (mirrored) this.imageT.setAttribute('transform', `translate(${W} 0) scale(-1 1)`); else this.imageT.removeAttribute('transform');
      this.imageG.style.display = '';
    } else {
      this.imageG.style.display = 'none';
    }
    // Hinge: inner face only (outside, the acetate hides it).
    this.hingeG.style.display = sc.hinge ? '' : 'none';
    if (sc.hinge) { this.hingeG.setAttribute('transform', sc.hinge.transform); sizeHinge(this.hingeG, sc.hinge); this.root.dataset.hinge = '1'; } else delete this.root.dataset.hinge;

    // Zone, ghost, text / logo
    const ink = inkById(view.state.inks[slot]);
    const active = view.active === slot;
    const isLogo = slot === 'il';
    const text = isLogo ? '' : view.state.texts[slot as 'ir' | 'el' | 'er'];
    const empty = !isLogo && text === '';
    const long = !isLogo && tooLong(text);
    const logoFree = Boolean(isLogo && view.state.logo.place && sc.logo);
    const free = Boolean(sc.text?.free) || logoFree;
    const warn = long || sc.issue !== null;
    this.zonePoly.setAttribute('points', zoneCorners(box));
    // Placed by hand: the zone gives way to the text's own outline.
    const zoneStroke = free ? 'transparent' : warn ? COLOUR_WARN : active ? COLOUR_ACTIVE : empty ? COLOUR_EMPTY : 'transparent';
    this.zonePoly.setAttribute('stroke', zoneStroke);
    this.zonePoly.setAttribute('stroke-width', active || warn ? '1.5' : '1');
    this.zonePoly.setAttribute('stroke-dasharray', empty && !active && !warn ? '3 3' : '');
    const outlined = free && sc.text ? sc.text : logoFree && sc.logo ? logoBox(sc.logo) : null;
    if (outlined) {
      this.textBox.setAttribute('points', textCorners(outlined, outlined.capPx * 0.25));
      this.textBox.setAttribute('stroke', warn ? COLOUR_WARN : active ? COLOUR_ACTIVE : 'transparent');
      this.textBox.setAttribute('stroke-width', '1.5');
      this.textBox.setAttribute('stroke-dasharray', active && !warn ? '4 3' : '');
      this.textBox.style.display = '';
    } else {
      this.textBox.style.display = 'none';
    }
    this.root.dataset.state = long ? 'long' : sc.issue ? 'issue' : active ? 'active' : empty ? 'empty' : 'set';
    this.root.dataset.issue = sc.issue ?? '';
    this.root.dataset.free = free ? '1' : '';
    this.root.dataset.active = String(active);
    this.root.dataset.slot = slot;
    this.root.dataset.movable = String(Boolean(active && this.movable()));
    this.root.classList.toggle('mt-temple--editable', true);

    const ghostOn = empty && !active;
    this.ghost.style.display = ghostOn ? '' : 'none';
    if (ghostOn) {
      this.ghost.textContent = this.opts.words.ghost;
      const size = Math.min(box.thick * 0.42, box.len / Math.max(8, this.opts.words.ghost.length) / 0.52);
      this.ghost.setAttribute('x', String(box.cx)); this.ghost.setAttribute('y', String(box.cy));
      this.ghost.setAttribute('font-size', size.toFixed(2));
      this.ghost.setAttribute('transform', `rotate(${box.angleDeg} ${box.cx} ${box.cy})`);
    }

    this.logoG.style.display = isLogo ? '' : 'none';
    this.textG.style.display = sc.text && sc.shown ? '' : 'none';
    this.placeSpin(sc, v, active);
    if (isLogo && sc.logo) {
      const l = sc.logo;
      const vb = LOGO_VIEWBOX.split(' ').map(Number) as [number, number, number, number];
      const k = l.height / vb[3];
      const place = (g: SVGGElement, dy: number) => g.setAttribute('transform', `rotate(${l.angleDeg} ${l.cx} ${l.cy}) translate(${l.x} ${l.y + dy}) scale(${k}) translate(${-vb[0]} ${-vb[1]})`);
      const paths = this.logoG.querySelector<SVGGElement>('.mt-temple-logo-paths')!;
      let shadow = this.logoG.querySelector<SVGGElement>('.mt-temple-logo-shadow');
      if (!shadow) {
        shadow = paths.cloneNode(true) as SVGGElement;
        shadow.setAttribute('class', 'mt-temple-logo-shadow');
        this.logoG.prepend(shadow);
      }
      place(shadow, l.height * INK_SHADOW_EM * 1.6);
      place(paths, 0);
      shadow.setAttribute('fill', ink.shadow);
      paths.setAttribute('fill', ink.color);
    }
    if (sc.text && sc.shown) {
      const t = sc.caption ?? sc.text;
      const b = baseline(t);
      for (const node of [this.shadowText, this.mainText]) {
        node.textContent = sc.shown;
        node.setAttribute('x', b.x.toFixed(2));
        node.setAttribute('text-anchor', 'middle');
        node.setAttribute('font-size', t.fontSize.toFixed(2));
        node.setAttribute('font-family', fontStack(sc.font));
        node.setAttribute('font-weight', String(sc.font.weight));
        node.setAttribute('letter-spacing', (sc.font.letterSpacing * t.fontSize).toFixed(2));
        node.setAttribute('transform', `rotate(${t.angleDeg.toFixed(2)} ${t.cx.toFixed(2)} ${t.cy.toFixed(2)})`);
      }
      this.mainText.setAttribute('y', b.y.toFixed(2));
      this.mainText.setAttribute('fill', ink.color);
      this.shadowText.setAttribute('y', (b.y + t.fontSize * INK_SHADOW_EM).toFixed(2));
      this.shadowText.setAttribute('fill', ink.shadow);
    }
  }

  // ------------------------------------------------------------ pointer

  /** Screen → drawing units (the viewBox scale). */
  private userScale(): number {
    const rect = this.svg.getBoundingClientRect();
    const vb = this.svg.viewBox.baseVal;
    return rect.width && vb.width ? vb.width / rect.width : 1;
  }

  /** A text that is written, or (A201) the Maison Mata logo, can be moved and turned. */
  private movable(): boolean {
    const slot = this.slot, sc = this.scene;
    if (!slot || !this.view || !this.opts.onPlace || !sc) return false;
    if (slot === LOCKED_SLOT) return Boolean(sc.logo);
    return Boolean(isEditable(slot) && this.view.state.texts[slot] && sc.text);
  }

  private currentPlace(): FreePlace | null {
    const slot = this.slot, sc = this.scene;
    if (!slot || !sc || !this.view) return null;
    if (slot === LOCKED_SLOT) return this.view.state.logo.place ?? (sc.text ? placeOf(sc.box, sc.text) : sc.logo ? logoPlace(sc.box, sc.logo) : null);
    if (!isEditable(slot) || !sc.text) return null;
    return this.view.state.style[slot].place ?? placeOf(sc.box, sc.text);
  }

  /** Centre of what is dragged, in drawing units. */
  private dragCentre(): { x: number; y: number } | null {
    const sc = this.scene;
    if (!sc) return null;
    if (sc.slot === LOCKED_SLOT) return sc.text ? { x: sc.text.cx, y: sc.text.cy } : sc.logo ? logoCentre(sc.logo) : null;
    return sc.text ? { x: sc.text.cx, y: sc.text.cy } : null;
  }

  /** Loupe: the view enlarged around the text or the logo (centre kept while the same view is shown). */
  private zoomView(sc: Scene, view: TempleView): { x: number; y: number; w: number; h: number } {
    const v = sc.view;
    const zoom = this.opts.zoom ?? 1;
    const key = `${sc.slot}·${view.templeId}`;
    if (!this.focus || this.focus.key !== key || this.drag === null && this.refocus) {
      const c = (sc.text ? { x: sc.text.cx, y: sc.text.cy } : sc.logo ? logoCentre(sc.logo) : null) ?? { x: sc.box.cx, y: sc.box.cy };
      this.focus = { x: c.x, y: c.y, key };
      this.refocus = false;
    }
    const w = v.w / zoom;
    const h = w / this.ratio;
    const x = Math.max(v.x, Math.min(v.x + v.w - w, this.focus.x - w / 2));
    return { x, y: this.focus.y - h / 2, w, h };
  }

  /** Loupe: centre the enlarged view on the text again (opening, « Recentrer », a quick position). */
  recentre(): void {
    this.refocus = true;
    if (this.view) this.render();
  }

  /**
   * A203: the turn handle — at the end of the selected text (or logo) nearer the middle of the card, along its axis,
   * a constant size on screen. Hidden when nothing can be turned (empty line, other slot, no card size yet).
   */
  private placeSpin(sc: Scene, v: { x: number; y: number; w: number; h: number }, active: boolean): void {
    const shown = active && this.movable() && Boolean(this.opts.onPlace);
    const layout = sc.text ?? (sc.logo ? logoBox(sc.logo) : null);
    const width = this.svg.getBoundingClientRect().width;
    if (!shown || !layout || !(width > 0)) { this.spinG.style.display = 'none'; delete this.root.dataset.spin; return; }
    const k = v.w / width; // drawing units per screen px
    const a = (layout.angleDeg * Math.PI) / 180;
    const towardMiddle = layout.cx > v.x + v.w / 2 ? -1 : 1;
    const reach = layout.width / 2 + layout.capPx * 0.25 + SPIN_GAP_PX * k;
    const x = layout.cx + towardMiddle * reach * Math.cos(a);
    const y = layout.cy + towardMiddle * reach * Math.sin(a);
    const r = SPIN_R_PX * k;
    this.spinDisc.setAttribute('cx', x.toFixed(2)); this.spinDisc.setAttribute('cy', y.toFixed(2)); this.spinDisc.setAttribute('r', r.toFixed(2));
    // A circular arrow (three quarters of a turn and its head), drawn in the disc.
    const q = r * 0.5;
    const p0 = { x: x + q, y }, p1 = { x, y: y - q };
    this.spinIcon.setAttribute('d', `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${q.toFixed(2)} ${q.toFixed(2)} 0 1 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} M ${(p1.x - q * 0.55).toFixed(2)} ${(p1.y - q * 0.45).toFixed(2)} L ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} L ${(p1.x - q * 0.5).toFixed(2)} ${(p1.y + q * 0.5).toFixed(2)}`);
    this.spinG.style.display = '';
    this.root.dataset.spin = '1';
  }

  /** A203: the drag on the turn handle — the angle swept around the text's centre is added to its tilt. */
  private installSpin(): void {
    const pointerAt = (e: PointerEvent) => {
      const rect = this.svg.getBoundingClientRect();
      const vb = this.svg.viewBox.baseVal;
      return { x: vb.x + ((e.clientX - rect.left) / rect.width) * vb.width, y: vb.y + ((e.clientY - rect.top) / rect.height) * vb.height };
    };
    this.spinG.addEventListener('pointerdown', e => {
      const slot = this.slot;
      if (!slot || !this.movable()) return;
      const place = this.currentPlace();
      const centre = this.dragCentre();
      if (!place || !centre) return;
      e.preventDefault();
      e.stopPropagation();
      const p = pointerAt(e);
      this.spin = { pointerId: e.pointerId, centre, angle0: Math.atan2(p.y - centre.y, p.x - centre.x), rot0: place.rot, start: place };
      this.root.dataset.spinning = '1';
      try { this.spinG.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    });
    this.spinG.addEventListener('pointermove', e => {
      const g = this.spin;
      const slot = this.slot;
      if (!g || e.pointerId !== g.pointerId || !slot) return;
      const p = pointerAt(e);
      let sweep = ((Math.atan2(p.y - g.centre.y, p.x - g.centre.x) - g.angle0) * 180) / Math.PI;
      sweep = ((sweep + 540) % 360) - 180; // the short way round
      let rot = Math.max(-ROT_MAX, Math.min(ROT_MAX, g.rot0 + sweep));
      if (Math.abs(rot) < ROT_SNAP) rot = 0;
      const cur = this.currentPlace() ?? g.start;
      this.opts.onPlace?.(slot, roundPlace({ ...cur, rot }));
    });
    const end = (e: PointerEvent) => {
      if (!this.spin || e.pointerId !== this.spin.pointerId) return;
      this.spin = null;
      delete this.root.dataset.spinning;
    };
    this.spinG.addEventListener('pointerup', end);
    this.spinG.addEventListener('pointercancel', end);
    // A click on the handle must not reach the card (it would open the keyboard on a phone).
    this.spinG.addEventListener('click', e => e.stopPropagation());
  }

  private installPointer(): void {
    this.hit.addEventListener('pointerdown', e => {
      const slot = this.slot;
      if (!slot || !this.view) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Second finger on a text being dragged: a turn.
      if (this.drag && this.pointers.size === 2 && this.movable()) {
        const [a, b] = [...this.pointers.values()];
        const place = this.currentPlace();
        if (a && b && place) this.turn = { pointerId: e.pointerId, angle0: Math.atan2(b.y - a.y, b.x - a.x), rot0: place.rot };
        try { this.hit.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
        return;
      }
      e.preventDefault();
      this.opts.onSelect?.(slot);
      if (!this.movable()) return;
      const place = this.currentPlace();
      const centre = this.dragCentre();
      if (!place || !centre) return;
      this.drag = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, start: place, centre, moved: false };
      try { this.hit.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    });
    this.hit.addEventListener('pointermove', e => {
      if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const d = this.drag;
      const slot = this.slot;
      const sc = this.scene;
      if (!d || !slot || !sc || !this.view) return;
      if (this.turn && this.pointers.size >= 2) {
        const [a, b] = [...this.pointers.values()];
        if (!a || !b) return;
        let rot = this.turn.rot0 + ((Math.atan2(b.y - a.y, b.x - a.x) - this.turn.angle0) * 180) / Math.PI;
        if (Math.abs(rot) < ROT_SNAP) rot = 0;
        d.moved = true;
        const cur = this.currentPlace();
        if (cur) this.opts.onPlace?.(slot, roundPlace({ ...cur, rot }));
        return;
      }
      if (e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
      if (!d.moved && Math.hypot(dx, dy) < 3) return;
      d.moved = true;
      const k = this.userScale();
      const x = d.centre.x + dx * k, y = d.centre.y + dy * k;
      // The centre stays on the temple; the edges may go past it (the outline turns coral and says so).
      if (!centreOnTemple(sc, x, y, this.view.image)) return;
      const cur = this.currentPlace();
      this.opts.onPlace?.(slot, roundPlace(pointPlace(sc.box as ZoneBox, x, y, cur?.rot ?? d.start.rot)));
    });
    const end = (e: PointerEvent) => {
      this.pointers.delete(e.pointerId);
      if (this.turn && (this.turn.pointerId === e.pointerId || this.pointers.size < 2)) this.turn = null;
      if (this.drag && this.drag.pointerId === e.pointerId) { this.dragged = this.drag.moved; this.drag = null; }
    };
    this.hit.addEventListener('pointerup', end);
    this.hit.addEventListener('pointercancel', end);
    // The click (a user gesture on every browser, which a programmatic focus needs on iOS) activates the slot unless the text was dragged.
    this.hit.addEventListener('click', () => {
      const slot = this.slot;
      const dragged = this.dragged; this.dragged = false;
      if (slot && !dragged) this.opts.onActivate?.(slot);
    });
  }
}

/** The logo's box as a text box (for its outline while it is placed by hand). */
function logoBox(logo: LogoLayout): TextLayout {
  const c = logoCentre(logo);
  return { cx: c.x, cy: c.y, angleDeg: logo.angleDeg, width: logo.width, capPx: logo.height, fontSize: 0 } as TextLayout;
}

// ------------------------------------------------------------ hinge drawing (inner view)

/** The rendered nickel hinge (David's photo, modelled in 3D), in the hinge's mm frame; sized by `sizeHinge`. */
function buildHinge(g: SVGGElement): void {
  const image = el('image', { class: 'mt-hinge-image', preserveAspectRatio: 'none', 'data-rivets': '2', 'data-rings': '6' });
  image.setAttribute('href', hingeUrl);
  g.append(image);
}

function sizeHinge(g: SVGGElement, p: HingePlacement): void {
  const image = g.firstElementChild;
  if (!image) return;
  const b = hingeImageBox(p);
  image.setAttribute('x', b.x.toFixed(3)); image.setAttribute('y', b.y.toFixed(3));
  image.setAttribute('width', b.w.toFixed(3)); image.setAttribute('height', b.h.toFixed(3));
}

export { placeHinge } from './scene';
